import { useEffect, useState } from 'react';
import type { Concert } from '@/mocks/concerts';
import { useAuth } from '@/context/AuthContext';
import { bookConcertTicket, parseConcertPrice } from '@/lib/tickets';

interface TicketBookingModalProps {
  concert: Concert | null;
  open: boolean;
  onClose: () => void;
}

function formatDate(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatMoney(amount: number): string {
  return `${amount.toLocaleString('fr-FR')} FCFA`;
}

function TicketBookingModal({ concert, open, onClose }: TicketBookingModalProps) {
  const { user, profile } = useAuth();
  const [tickets, setTickets] = useState(1);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const unitPrice = concert ? parseConcertPrice(concert.price) : 0;
  const isFree = unitPrice === 0;
  const total = unitPrice * tickets;

  useEffect(() => {
    if (open) {
      setTickets(1);
      setError('');
      setSuccess(false);
      setName(profile?.full_name || '');
      setPhone(profile?.phone || '');
    }
  }, [open, profile]);

  if (!open || !concert) return null;

  const handleClose = () => {
    setSuccess(false);
    setError('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!user) {
      setError('Vous devez être connecté pour réserver un billet.');
      return;
    }
    if (!name.trim()) {
      setError('Veuillez indiquer votre nom.');
      return;
    }
    if (!phone.trim()) {
      setError('Veuillez indiquer votre numéro de téléphone.');
      return;
    }

    setLoading(true);
    try {
      await bookConcertTicket({
        concertId: concert.id,
        artistName: concert.artist_name,
        concertTitle: concert.title,
        venue: concert.venue,
        location: concert.location,
        concertDate: concert.date,
        tickets,
        unitPrice,
        customerName: name.trim(),
        customerPhone: phone.trim(),
      });
      setSuccess(true);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Une erreur est survenue lors de la réservation.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background-950/70 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div className="relative w-full max-w-md bg-background-50 border border-background-200/70 rounded-xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70 shrink-0">
          <div className="flex items-center gap-2">
            <i className="ri-ticket-2-line text-accent-400 text-xl" />
            <h3 className="font-heading font-semibold text-lg text-foreground-50">
              Réserver un billet
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center text-foreground-500 hover:text-foreground-50 transition-colors"
            aria-label="Fermer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {success ? (
          <div className="p-8 flex flex-col items-center text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-500/10 mb-4">
              <i className="ri-check-line text-3xl text-primary-400" />
            </div>
            <h4 className="font-heading font-semibold text-lg text-foreground-50 mb-2">
              Réservation confirmée !
            </h4>
            <p className="text-sm text-foreground-400 mb-1">
              {tickets} billet{tickets > 1 ? 's' : ''} pour{' '}
              <span className="font-semibold text-foreground-50">{concert.artist_name}</span>
            </p>
            <p className="text-sm text-foreground-400 mb-5">
              {formatDate(concert.date)} · {concert.venue}
            </p>
            {!isFree && (
              <p className="text-sm font-semibold text-foreground-50 mb-5">
                Total : {formatMoney(total)}
              </p>
            )}
            <button
              onClick={handleClose}
              className="px-6 py-2.5 rounded-md bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 transition-colors whitespace-nowrap"
            >
              Terminer
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto">
            {/* Résumé du concert */}
            <div className="flex gap-3 p-3 rounded-lg bg-background-100 border border-background-200/70">
              <img
                src={concert.poster_url}
                alt={`${concert.artist_name} — ${concert.title}`}
                className="w-16 h-20 object-cover object-top rounded-md shrink-0"
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground-50 leading-tight">
                  {concert.artist_name}
                </p>
                <p className="text-xs text-foreground-400 line-clamp-2">{concert.title}</p>
                <p className="text-xs text-foreground-400 mt-1 capitalize">
                  <i className="ri-calendar-line mr-1" />
                  {formatDate(concert.date)}
                </p>
                <p className="text-xs text-foreground-400">
                  <i className="ri-map-pin-line mr-1" />
                  {concert.venue} · {concert.location}
                </p>
              </div>
            </div>

            {/* Nombre de billets */}
            <div>
              <label className="block text-sm font-medium text-foreground-300 mb-2">
                Nombre de billets
              </label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setTickets((t) => Math.max(1, t - 1))}
                  disabled={tickets <= 1}
                  className="w-10 h-10 flex items-center justify-center rounded-md bg-background-100 text-foreground-50 hover:bg-background-200/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Réduire"
                >
                  <i className="ri-subtract-line" />
                </button>
                <span className="w-12 text-center text-lg font-semibold text-foreground-50">
                  {tickets}
                </span>
                <button
                  type="button"
                  onClick={() => setTickets((t) => Math.min(10, t + 1))}
                  disabled={tickets >= 10}
                  className="w-10 h-10 flex items-center justify-center rounded-md bg-background-100 text-foreground-50 hover:bg-background-200/60 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Augmenter"
                >
                  <i className="ri-add-line" />
                </button>
              </div>
            </div>

            {/* Nom */}
            <div>
              <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                Nom complet *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Votre nom"
                className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>

            {/* Téléphone */}
            <div>
              <label className="block text-sm font-medium text-foreground-300 mb-1.5">
                Numéro de téléphone *
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+228 …"
                className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300/60 text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>

            {/* Total */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-accent-100/10 border border-accent-400/20">
              <span className="text-sm text-foreground-400">
                {tickets} billet{tickets > 1 ? 's' : ''}
                {!isFree && ` × ${formatMoney(unitPrice)}`}
              </span>
              <span className="text-base font-semibold text-foreground-50">
                {isFree ? 'Gratuit' : formatMoney(total)}
              </span>
            </div>

            {!user && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-background-100 border border-background-200/70">
                <i className="ri-login-box-line text-foreground-500 mt-0.5 shrink-0" />
                <p className="text-sm text-foreground-400">
                  Vous devez être connecté pour finaliser la réservation.{' '}
                  <a
                    href="/auth/login"
                    className="text-primary-400 font-medium hover:underline"
                  >
                    Se connecter
                  </a>
                </p>
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-accent-100/10 border border-accent-400/20">
                <i className="ri-error-warning-line text-accent-400 mt-0.5 shrink-0" />
                <p className="text-sm text-accent-400">{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-3 rounded-md bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Réservation en cours…
                </>
              ) : (
                <>
                  <i className="ri-ticket-2-line" />
                  Confirmer la réservation
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default TicketBookingModal;